import { Address } from '@ton/core';
import type {
  AssetId,
  ChainAddress,
  Quote,
  QuoteRequest,
  TonTransaction,
} from '@ston-fi/omniston-sdk-react';
import { SWAP_PRMD_MASTER } from './config';

export const SWAP_DECIMALS = 9;
export const SWAP_QUOTE_MAX_AGE_SECONDS = 30;
export const SWAP_MAX_MESSAGES = 4;
const PIPS_PER_PERCENT = 10_000;
const NANO_TON = 1_000_000_000n;
const ALLOWED_PROTOCOLS = new Set(['StonFiV2', 'DeDust', 'SwapCoffee', 'Coffee']);

export type SwapDirection = 'tonToPrmd' | 'prmdToTon';

export const TON_ASSET: AssetId = {
  chain: {
    $case: 'ton',
    value: {
      kind: {
        $case: 'native',
        value: {},
      },
    },
  },
};

/** Omniston AssetId for an arbitrary jetton master. */
export function jettonAsset(master: string): AssetId {
  return {
    chain: {
      $case: 'ton',
      value: {
        kind: {
          $case: 'jetton',
          value: master,
        },
      },
    },
  };
}

export const PRMD_ASSET: AssetId = jettonAsset(SWAP_PRMD_MASTER);

export function tonChainAddress(value: string): ChainAddress {
  return {
    chain: {
      $case: 'ton',
      value,
    },
  };
}

export function parseSwapUnits(value: string, decimals = SWAP_DECIMALS): bigint | null {
  const trimmed = value.trim();
  if (!/^\d*(?:\.\d*)?$/.test(trimmed) || !/[1-9]/.test(trimmed)) return null;

  const [whole = '0', fraction = ''] = trimmed.split('.');
  if (fraction.length > decimals) return null;

  const normalizedWhole = whole || '0';
  return (
    BigInt(normalizedWhole) * 10n ** BigInt(decimals) +
    BigInt((fraction + '0'.repeat(decimals)).slice(0, decimals))
  );
}

export function formatSwapUnits(value: bigint | string, decimals = SWAP_DECIMALS): string {
  const units = typeof value === 'bigint' ? value : BigInt(value);
  const base = 10n ** BigInt(decimals);
  const whole = units / base;
  const fraction = (units % base).toString().padStart(decimals, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function createSwapQuoteRequest(
  direction: SwapDirection,
  inputUnits: bigint,
  slippagePercent: number,
  integrator?: { address: string; feePips: number },
): QuoteRequest {
  return createAssetQuoteRequest(
    direction === 'tonToPrmd' ? TON_ASSET : PRMD_ASSET,
    direction === 'tonToPrmd' ? PRMD_ASSET : TON_ASSET,
    inputUnits,
    slippagePercent,
    integrator,
  );
}

export function createAssetQuoteRequest(
  inputAsset: AssetId,
  outputAsset: AssetId,
  inputUnits: bigint,
  slippagePercent: number,
  integrator?: { address: string; feePips: number },
): QuoteRequest {
  if (!Number.isFinite(slippagePercent) || slippagePercent <= 0 || slippagePercent > 5) {
    throw new Error('Slippage must be between 0 and 5 percent');
  }
  if (inputUnits <= 0n) throw new Error('Swap input must be positive');

  const request: QuoteRequest = {
    inputAsset,
    outputAsset,
    amount: {
      $case: 'inputUnits',
      value: inputUnits.toString(),
    },
    settlementParams: [
      {
        params: {
          $case: 'swap',
          value: {
            maxPriceSlippagePips: Math.round(slippagePercent * PIPS_PER_PERCENT),
            maxRoutes: SWAP_MAX_MESSAGES,
            flexibleIntegratorFee: true,
            allowRiskyRoutes: false,
          },
        },
      },
    ],
  };

  if (integrator?.address && integrator.feePips > 0) {
    request.integratorAddress = tonChainAddress(integrator.address);
    request.integratorFeePips = integrator.feePips;
  }

  return request;
}

export function isExpectedAsset(asset: AssetId, expected: AssetId): boolean {
  if (asset.chain.$case !== 'ton' || expected.chain.$case !== 'ton') return false;
  const actualKind = asset.chain.value.kind;
  const expectedKind = expected.chain.value.kind;
  if (actualKind.$case !== expectedKind.$case) return false;
  if (actualKind.$case === 'native') return true;
  if (expectedKind.$case !== 'jetton') return false;

  return Address.parse(actualKind.value).equals(Address.parse(expectedKind.value));
}

export function validateSwapQuote(
  quote: Quote,
  direction: SwapDirection,
  expectedInputUnits: bigint,
  nowSeconds = Math.floor(Date.now() / 1000),
  expectedIntegratorAddress = '',
): void {
  validateAssetSwapQuote(
    quote,
    direction === 'tonToPrmd' ? TON_ASSET : PRMD_ASSET,
    direction === 'tonToPrmd' ? PRMD_ASSET : TON_ASSET,
    expectedInputUnits,
    nowSeconds,
    expectedIntegratorAddress,
  );
}

export function validateAssetSwapQuote(
  quote: Quote,
  expectedInput: AssetId,
  expectedOutput: AssetId,
  expectedInputUnits: bigint,
  nowSeconds = Math.floor(Date.now() / 1000),
  expectedIntegratorAddress = '',
): void {
  if (!isExpectedAsset(quote.inputAsset, expectedInput)) {
    throw new Error('Unexpected input asset in quote');
  }
  if (!isExpectedAsset(quote.outputAsset, expectedOutput)) {
    throw new Error('Unexpected output asset in quote');
  }
  if (BigInt(quote.inputUnits) !== expectedInputUnits) {
    throw new Error('Quote amount no longer matches the form');
  }
  if (quote.settlementData.$case !== 'swap') {
    throw new Error('Only immediate swaps are allowed');
  }
  if (BigInt(quote.outputUnits) <= 0n) {
    throw new Error('Quote output must be positive');
  }
  if (nowSeconds - quote.quoteTimestamp > SWAP_QUOTE_MAX_AGE_SECONDS) {
    throw new Error('Quote expired');
  }
  if (quote.quoteTimestamp - nowSeconds > 5) {
    throw new Error('Quote timestamp is in the future');
  }
  if (expectedIntegratorAddress) {
    if (
      quote.integratorAddress?.chain.$case !== 'ton' ||
      !Address.parse(quote.integratorAddress.chain.value).equals(
        Address.parse(expectedIntegratorAddress),
      )
    ) {
      throw new Error('Unexpected integrator address');
    }
  } else if (quote.integratorAddress || BigInt(quote.integratorFeeUnits) > 0n) {
    throw new Error('Unexpected integrator fee');
  }

  const settlement = quote.settlementData.value;
  if (BigInt(settlement.minOutputAmount) <= 0n) {
    throw new Error('Minimum output must be positive');
  }
  if (BigInt(settlement.minOutputAmount) > BigInt(quote.outputUnits)) {
    throw new Error('Minimum output exceeds quoted output');
  }
  if (settlement.routes.length === 0 || settlement.routes.length > SWAP_MAX_MESSAGES) {
    throw new Error('Unexpected route count');
  }
  for (const route of settlement.routes) {
    if (route.steps.length === 0) throw new Error('Swap route has no steps');
    for (const step of route.steps) {
      if (step.chunks.length === 0) throw new Error('Swap step has no chunks');
      for (const chunk of step.chunks) {
        if (!ALLOWED_PROTOCOLS.has(chunk.protocol)) {
          throw new Error(`Unsupported swap protocol: ${chunk.protocol}`);
        }
        if (BigInt(chunk.inputUnits) <= 0n || BigInt(chunk.outputUnits) <= 0n) {
          throw new Error('Swap route contains invalid amounts');
        }
      }
    }
  }
}

export interface TonTransactionValidation {
  direction: SwapDirection;
  inputUnits: bigint;
  gasBudget?: string;
}

export function validateTonTransaction(
  transaction: TonTransaction,
  validation: TonTransactionValidation,
): void {
  validateTonTransactionUnits(transaction, {
    tonInputUnits: validation.direction === 'tonToPrmd' ? validation.inputUnits : 0n,
    gasBudget: validation.gasBudget,
  });
}

export function validateTonTransactionUnits(
  transaction: TonTransaction,
  validation: { tonInputUnits: bigint; gasBudget?: string },
): void {
  if (transaction.messages.length === 0 || transaction.messages.length > SWAP_MAX_MESSAGES) {
    throw new Error('Unexpected transaction message count');
  }

  let totalTon = 0n;
  for (const message of transaction.messages) {
    Address.parse(message.targetAddress);
    const sendAmount = BigInt(message.sendAmount);
    if (sendAmount <= 0n) throw new Error('Transaction message amount must be positive');
    if (!message.payload || !/^(?:[0-9a-fA-F]{2})+$/.test(message.payload)) {
      throw new Error('Transaction payload is not valid hex');
    }
    if (
      message.jettonWalletStateInit &&
      !/^(?:[0-9a-fA-F]{2})*$/.test(message.jettonWalletStateInit)
    ) {
      throw new Error('Transaction state init is not valid hex');
    }
    totalTon += sendAmount;
  }

  const gasBudget = validation.gasBudget ? BigInt(validation.gasBudget) : 300_000_000n;
  const expectedTon = validation.tonInputUnits;
  const maxTon = expectedTon + gasBudget + NANO_TON / 10n;

  if (expectedTon > 0n && totalTon < expectedTon) {
    throw new Error('Transaction does not include the exact TON input');
  }
  if (totalTon > maxTon) {
    throw new Error('Transaction gas budget exceeds the allowed limit');
  }
}

export function hexToBase64(value: string): string {
  return Buffer.from(value, 'hex').toString('base64');
}

export function calculateConstantProductOutput(
  inputUnits: bigint,
  reserveIn: bigint,
  reserveOut: bigint,
  feeBps: bigint,
): bigint {
  if (
    inputUnits <= 0n ||
    reserveIn <= 0n ||
    reserveOut <= 0n ||
    feeBps < 0n ||
    feeBps >= 10_000n
  ) {
    return 0n;
  }
  const feeDenominator = 10_000n;
  const inputAfterFee = (inputUnits * (feeDenominator - feeBps)) / feeDenominator;
  return (reserveOut * inputAfterFee) / (reserveIn + inputAfterFee);
}

export function applySlippage(outputUnits: bigint, slippagePercent: number): bigint {
  if (
    outputUnits <= 0n ||
    !Number.isFinite(slippagePercent) ||
    slippagePercent <= 0 ||
    slippagePercent > 5
  ) {
    return 0n;
  }
  const slippageBps = BigInt(Math.round(slippagePercent * 100));
  return (outputUnits * (10_000n - slippageBps)) / 10_000n;
}
